import { useState } from "react";
import dayjs from "dayjs";

const REPLY_PREVIEW_MAX_LENGTH = 60;

const formatTemplateDisplayName = (templateName, body) => {
  if (templateName) {
    return String(templateName)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  if (typeof body === "string" && body.startsWith("Template: ")) {
    return body.slice("Template: ".length).trim() || "Template Message";
  }
  return "Template Message";
};

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const cleanTemplateBody = (value) => {
  const lines = String(value || "").split(/\r?\n/);
  const cleaned = [];
  let skippingDump = false;

  for (const line of lines) {
    const trimmed = line.trim().toLowerCase();
    if (trimmed.startsWith("template_values:")) {
      skippingDump = true;
      continue;
    }
    if (trimmed.startsWith("buttons:")) {
      skippingDump = false;
      continue;
    }
    if (!skippingDump) cleaned.push(line);
  }

  return cleaned.join("\n").trim();
};

const getTemplateParameterEntries = (parameters) => {
  if (!parameters) return [];
  const values = isPlainObject(parameters.template_values) ? parameters.template_values : parameters;
  return Object.entries(values).filter(([key, value]) => {
    if (["buttons", "template_values", "language_code"].includes(key)) return false;
    return value !== undefined && value !== null && value !== "";
  });
};

const normalizeTemplateKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const getTemplateValues = (parameters) => {
  if (!parameters) return {};
  return isPlainObject(parameters.template_values) ? parameters.template_values : parameters;
};

const resolveTemplateBody = (body, parameters) => {
  let resolved = cleanTemplateBody(body);
  const values = getTemplateValues(parameters);
  const entries = Object.entries(values).filter(([key, value]) => {
    if (["buttons", "template_values", "language_code"].includes(key)) return false;
    return value !== undefined && value !== null && value !== "";
  });

  if (!resolved || entries.length === 0) return resolved;

  const valueByKey = entries.reduce((acc, [key, value]) => {
    const formattedValue = formatParameterValue(value);
    acc[String(key)] = formattedValue;
    acc[normalizeTemplateKey(key)] = formattedValue;
    return acc;
  }, {});

  resolved = resolved.replace(/{{\s*(\d+)\s*}}/g, (match, index) => {
    const numericValue = valueByKey[String(index)];
    if (numericValue !== undefined) return numericValue;
    const positionalValue = entries[Number(index) - 1]?.[1];
    return positionalValue !== undefined ? formatParameterValue(positionalValue) : match;
  });

  resolved = resolved.replace(/{\s*([A-Za-z0-9_ -]+)\s*}/g, (match, key) => {
    const value = valueByKey[String(key)] ?? valueByKey[normalizeTemplateKey(key)];
    return value !== undefined ? value : match;
  });

  resolved = resolved.replace(/<\s*([^<>]+?)\s*>/g, (match, key) => {
    const value = valueByKey[String(key)] ?? valueByKey[normalizeTemplateKey(key)];
    return value !== undefined ? value : match;
  });

  return resolved;
};

const formatParameterValue = (value) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => formatParameterValue(item)).filter(Boolean).join(", ");
  }
  if (isPlainObject(value)) {
    return Object.entries(value)
      .map(([key, item]) => `${key}: ${formatParameterValue(item)}`)
      .join(", ");
  }
  return String(value);
};

const ChatBubble = ({ msg, repliedMessage, previewImage, setPreviewImage }) => {
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const isAdmin = msg.sender === "admin";
  const isFailed = msg.status === "failed";
  const isTemplate = msg.message_type === "template";
  const templateDisplayName = formatTemplateDisplayName(msg.template_name, msg.body);
  const templateBody = resolveTemplateBody(msg.full_message || msg.body || msg.text || msg.message, msg.template_variables);
  const templateParameterEntries = getTemplateParameterEntries(msg.template_variables);
  const mediaUrl = msg.media_url || (typeof msg.body === "string" && /^https?:\/\//i.test(msg.body) ? msg.body : null);
  const fileUrl = mediaUrl || msg.text;
  const captionText = msg.caption || "";
  const isImage = msg.type === "image";
  const isVideo = msg.type === "video";
  const isDocument = msg.type === "document";
  const fileName = (() => {
    try {
      return decodeURIComponent((fileUrl || "").split("/").pop() || "document");
    } catch {
      return "document";
    }
  })();
  const failureDetail =
    msg.error_message ||
    (msg.error_code ? `Error ${msg.error_code}` : null) ||
    (typeof msg.provider_response === "string"
      ? msg.provider_response
      : msg.provider_response
      ? JSON.stringify(msg.provider_response)
      : null) ||
    "Message failed to send";

  // Real delivery state from Meta receipts; falls back to legacy booleans/status.
  const deliveryState =
    isFailed || msg.failed_at
      ? "failed"
      : msg.read_at || msg.is_read === 1 || msg.is_read === true || msg.status === "read"
      ? "read"
      : msg.delivered_at || msg.is_delivered === 1 || msg.is_delivered === true || msg.status === "delivered"
      ? "delivered"
      : "sent";
  const tickColor =
    deliveryState === "read"
      ? "text-white"
      : deliveryState === "failed"
      ? "text-red-300"
      : "text-blue-200/60";
  const statusTitle =
    deliveryState === "read"
      ? msg.read_at
        ? `Read ${dayjs(msg.read_at).format("DD MMM HH:mm")}`
        : "Read"
      : deliveryState === "delivered"
      ? msg.delivered_at
        ? `Delivered ${dayjs(msg.delivered_at).format("DD MMM HH:mm")}`
        : "Delivered"
      : deliveryState === "failed"
      ? `Failed${msg.error_message ? `: ${msg.error_message}` : ""}`
      : "Sent";
  const renderTicks = () =>
    deliveryState === "failed" ? (
      <span className="font-bold">✕</span>
    ) : deliveryState === "sent" ? (
      <span>✓</span>
    ) : (
      <span className="flex -space-x-[3px] font-bold text-sm"><span>✓</span><span>✓</span></span>
    );

  const reactions = Array.isArray(msg.reactions) ? msg.reactions : [];
  const reactionSummary = reactions.reduce((acc, reaction) => {
    if (!reaction?.emoji) return acc;
    acc[reaction.emoji] = (acc[reaction.emoji] || 0) + 1;
    return acc;
  }, {});
  const repliedPreviewFullText = (() => {
    if (!repliedMessage) return "";
    if (repliedMessage.type === "image") return "Image";
    if (repliedMessage.type === "video") return "Video";
    if (repliedMessage.type === "document") return "Document";
    if (repliedMessage.message_type === "template") {
      const resolvedTemplate = resolveTemplateBody(
        repliedMessage.full_message || repliedMessage.body || repliedMessage.text || repliedMessage.message,
        repliedMessage.template_variables,
      );
      return resolvedTemplate || formatTemplateDisplayName(repliedMessage.template_name, repliedMessage.body);
    }
    return (repliedMessage.text || repliedMessage.body || "Message").replace(/\s+/g, " ").trim();
  })();
  const repliedPreviewText =
    repliedPreviewFullText.length > REPLY_PREVIEW_MAX_LENGTH
      ? `${repliedPreviewFullText.slice(0, REPLY_PREVIEW_MAX_LENGTH)}...`
      : repliedPreviewFullText;
  return (
    <>
      <div className="relative group">
        <div
          className={`relative w-fit max-w-[90%] xs:max-w-[85%] sm:max-w-[75%] md:max-w-[65%] px-3 sm:px-4 py-2 sm:py-2.5 rounded-2xl shadow-sm mb-2 text-sm leading-relaxed border ${isAdmin
            ? "bg-blue-600 text-white self-end ml-auto rounded-tr-none shadow-blue-500/10"
            : "bg-slate-800 text-slate-100 self-start mr-auto rounded-tl-none border border-slate-700/50 shadow-black/20"
          } ${isFailed ? "border-red-500 ring-2 ring-red-500/50 bg-red-950/20" : isAdmin ? "border-transparent" : "border-slate-700/50"}`}
        >
          {isFailed && (
            <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center border border-red-300">
              !
            </div>
          )}
        {/* Tail (Pseudo-element like) - Optional, simplified with rounded corners above */}

        {/* Message content */}
        {(msg.is_forwarded || msg.is_frequently_forwarded) && (
          <div className={`mb-1.5 text-[10px] font-semibold flex items-center gap-1 ${isAdmin ? "text-blue-100/80" : "text-slate-300"}`}>
            <span>↪</span>
            <span>{msg.is_frequently_forwarded ? "Frequently forwarded" : "Forwarded"}</span>
          </div>
        )}

        {msg.reply_to_message_id && (
          <div className={`mb-2 rounded-lg px-2.5 py-1.5 border-l-2 ${isAdmin ? "bg-blue-500/25 border-blue-200/70" : "bg-slate-700/70 border-slate-300/60"}`}>
            <p className={`text-[10px] font-semibold ${isAdmin ? "text-blue-100" : "text-slate-200"}`}>
              Reply
            </p>
            <p className={`text-xs truncate ${isAdmin ? "text-blue-50/95" : "text-slate-300"}`} title={repliedPreviewFullText || msg.reply_to_message_id}>
              {repliedPreviewText || `Message (${msg.reply_to_message_id})`}
            </p>
          </div>
        )}

        {isImage && fileUrl ? (
          <div className="relative -mx-1.5 -mt-1.5 sm:-mx-2 sm:-mt-2">
            <img
              src={fileUrl}
              alt="sent media"
              className="rounded-xl max-w-[200px] xs:max-w-[240px] sm:max-w-[280px] max-h-60 xs:max-h-72 sm:max-h-80 cursor-pointer block object-cover shadow-sm ring-1 ring-black/10"
              onClick={() => setPreviewImage(fileUrl)}
            />
            {captionText && (
              <div className="px-2 py-1.5 text-xs text-white/90 break-words whitespace-pre-wrap">
                {captionText}
              </div>
            )}
            {/* Gradient overlay for timestamp readability */}
            <div className="absolute bottom-0 right-0 w-full h-16 bg-gradient-to-t from-black/80 via-black/40 to-transparent rounded-b-xl"></div>

            {/* Timestamp overlay for images */}
            <div className="absolute bottom-1.5 right-2 text-white/90 text-[10px] flex items-center gap-1 font-medium">
              <span>{dayjs(msg.timestamp).format("HH:mm")}</span>
              {isAdmin && (
                <span className={tickColor} title={statusTitle}>{renderTicks()}</span>
              )}
            </div>
          </div>
        ) : isVideo && fileUrl ? (
          <div className="space-y-2">
            <video
              src={fileUrl}
              controls
              preload="metadata"
              className="rounded-xl max-w-[260px] sm:max-w-[320px] max-h-80 bg-black"
            />
            {captionText && (
              <p className="text-xs opacity-90 break-words whitespace-pre-wrap">{captionText}</p>
            )}
          </div>
        ) : isDocument && fileUrl ? (
          <div className="flex items-start gap-3 min-w-[240px] sm:min-w-[280px]">
            <div className="w-10 h-10 rounded-lg bg-slate-700/70 flex items-center justify-center text-white font-bold">
              PDF
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" title={fileName}>{fileName}</p>
              {captionText && (
                <p className="text-xs opacity-80 mt-1 break-words whitespace-pre-wrap">{captionText}</p>
              )}
              <div className="mt-2 flex gap-2">
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] px-2 py-1 rounded-md bg-slate-700/70 hover:bg-slate-700 text-white"
                >
                  Open
                </a>
                <a
                  href={fileUrl}
                  download
                  className="text-[11px] px-2 py-1 rounded-md bg-slate-700/70 hover:bg-slate-700 text-white"
                >
                  Download
                </a>
              </div>
            </div>
          </div>
        ) : isTemplate ? (
          <button
            type="button"
            onClick={() => setTemplateModalOpen(true)}
            className="w-full text-left space-y-1 relative pb-6 rounded-lg transition-colors hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50"
            title="View full template message"
          >
            <div className="flex items-center gap-2 pr-12">
              <span className="text-base leading-none" aria-hidden="true">📧</span>
              <span className="text-sm font-semibold break-words">{templateDisplayName}</span>
            </div>
            <p className={`text-[10px] ${isAdmin ? "text-blue-100/60" : "text-slate-500"}`}>
              Tap to view full message
            </p>

            <div className={`absolute bottom-0 right-0 flex items-center gap-0.5 text-[10px] ${isAdmin ? "text-blue-100/70" : "text-slate-400"}`}>
              <span className="mr-0.5 font-medium tracking-wide">{dayjs(msg.sent_at || msg.timestamp).format("HH:mm")}</span>
              {isAdmin && (
                <span className={tickColor} title={statusTitle}>{renderTicks()}</span>
              )}
            </div>
          </button>
        ) : (
          <div className="relative pr-12 min-h-[22px] break-words whitespace-pre-wrap font-normal" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {msg.text}
            {/* Inline timestamp for text messages */}
            <div className={`absolute bottom-0 right-0 flex items-center gap-0.5 text-[10px] ml-2 ${isAdmin
                ? "text-blue-100/70"
                : "text-slate-400"
              }`}>
              <span className="mr-0.5 font-medium tracking-wide">{dayjs(msg.timestamp).format("HH:mm")}</span>
              {isAdmin && (
                <span className={tickColor} title={statusTitle}>{renderTicks()}</span>
              )}
            </div>
          </div>
        )}
        </div>

        {isFailed && (
          <div className="absolute z-30 -top-2 right-0 max-w-md hidden group-hover:block bg-slate-900 text-slate-100 text-[10px] px-3 py-2 rounded-lg border border-slate-600 shadow-xl">
            <div>
              <div className="text-red-400 font-semibold mb-1">❌ Failed Message</div>
              <div className="whitespace-pre-wrap break-words">{failureDetail}</div>
            </div>
          </div>
        )}

        {Object.keys(reactionSummary).length > 0 && (
          <div className={`absolute -bottom-4 ${isAdmin ? "right-2" : "left-2"} flex items-center gap-1 bg-slate-900 border border-slate-700 rounded-full px-2 py-0.5 shadow-md`}>
            {Object.entries(reactionSummary).map(([emoji, count]) => (
              <span key={emoji} className="text-xs leading-none" title={`${emoji} ${count}`}>
                {emoji}{count > 1 ? ` ${count}` : ""}
              </span>
            ))}
          </div>
        )}
      </div>

      {templateModalOpen && isTemplate && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setTemplateModalOpen(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-lg max-h-[85vh] overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl flex flex-col"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="template-modal-title"
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-800 px-5 py-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">WhatsApp Template</p>
                <h3 id="template-modal-title" className="mt-1 text-lg font-bold text-slate-100">
                  {templateDisplayName}
                </h3>
                {msg.template_language && (
                  <p className="mt-1 text-xs text-slate-500">Language: {msg.template_language}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setTemplateModalOpen(false)}
                className="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-800 hover:text-slate-100"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 custom-scrollbar">
              {templateBody && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Message</p>
                  <div className="rounded-xl border border-slate-800 bg-slate-900/80 px-4 py-3 text-sm text-slate-200 whitespace-pre-wrap break-words leading-relaxed">
                    {templateBody}
                  </div>
                </div>
              )}

              {templateParameterEntries.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Parameters</p>
                  <div className="flex flex-wrap gap-2">
                    {templateParameterEntries.map(([key, value]) => {
                      const stringValue = formatParameterValue(value);
                      const isUrl = /^https?:\/\//i.test(stringValue);
                      return (
                        <span
                          key={key}
                          className="inline-flex max-w-full flex-col gap-0.5 rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs text-blue-100"
                        >
                          <span className="font-semibold text-blue-300">{key}</span>
                          {isUrl ? (
                            <a
                              href={stringValue}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="break-all underline underline-offset-2 hover:opacity-80"
                            >
                              {stringValue}
                            </a>
                          ) : (
                            <span className="break-words text-slate-200">{stringValue}</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="text-[10px] text-slate-600 space-y-0.5">
                <p>Sent {dayjs(msg.sent_at || msg.timestamp).format("DD MMM YYYY, HH:mm")}</p>
                {msg.delivered_at && (
                  <p>Delivered {dayjs(msg.delivered_at).format("DD MMM YYYY, HH:mm")}</p>
                )}
                {msg.read_at && (
                  <p className="text-blue-500">Read {dayjs(msg.read_at).format("DD MMM YYYY, HH:mm")}</p>
                )}
                {(msg.failed_at || msg.status === "failed") && (
                  <p className="text-red-500">
                    Failed{msg.error_message ? `: ${msg.error_message}` : msg.error_code ? ` (${msg.error_code})` : ""}
                  </p>
                )}
                {!msg.delivered_at && !msg.read_at && !msg.failed_at && msg.status && msg.status !== "sent" && msg.status !== "failed" && (
                  <p>{msg.status}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBubble;
